import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDateRange } from "@/context/DateRangeContext";
import { PageHeader, KpiCard, BranchSelect, SaveStatus, rm } from "@/components/common";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronRight, Plus, Settings2 } from "lucide-react";

function RegistryManager({ open, setOpen, methods, banks, branches, refresh }) {
  const [method, setMethod] = useState({ display_name: "", requires_bank_reconciliation: false, applicable_branch_ids: [] });
  const [bank, setBank] = useState({ name: "", aliases: "", currency: "MYR" });
  const addMethod = async () => { if (!method.display_name.trim()) return; try { await api.post("/sales/methods", method); setMethod({ display_name: "", requires_bank_reconciliation: false, applicable_branch_ids: [] }); toast.success("Sales method registered"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const addBank = async () => { if (!bank.name.trim()) return; try { await api.post("/sales/banks", { ...bank, aliases: bank.aliases.split(",").map((v) => v.trim()).filter(Boolean) }); setBank({ name: "", aliases: "", currency: "MYR" }); toast.success("Bank account added"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const toggleMethod = async (entry) => { try { await api.put(`/sales/methods/${entry.id}/status`, { active: entry.status !== "active" }); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const toggleBank = async (entry) => { try { await api.put(`/sales/banks/${entry.id}/status`, { active: !entry.active }); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const toggleBranch = (id) => setMethod((current) => ({ ...current, applicable_branch_ids: current.applicable_branch_ids.includes(id) ? current.applicable_branch_ids.filter((value) => value !== id) : [...current.applicable_branch_ids, id] }));
  return <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Sales Registry & Bank Accounts</DialogTitle></DialogHeader>
    <section className="space-y-3"><h3 className="font-semibold">Add Sales Method</h3><div className="grid sm:grid-cols-2 gap-3"><div><Label>Name</Label><Input value={method.display_name} onChange={(e) => setMethod({ ...method, display_name: e.target.value })} placeholder="e.g. E-wallet" /></div><label className="flex items-center gap-2 pt-6"><Checkbox checked={method.requires_bank_reconciliation} onCheckedChange={(value) => setMethod({ ...method, requires_bank_reconciliation: !!value })} />Requires bank reconciliation</label></div><div><Label>Applicable branches (blank = all)</Label><div className="flex flex-wrap gap-2 mt-1">{branches.map((branch) => <label key={branch.id} className="flex items-center gap-1 rounded border px-2 py-1 text-xs"><Checkbox checked={method.applicable_branch_ids.includes(branch.id)} onCheckedChange={() => toggleBranch(branch.id)} />{branch.name}</label>)}</div></div><Button onClick={addMethod}><Plus className="h-4 w-4 mr-1" />Add Method</Button><div className="divide-y rounded border">{methods.map((entry) => <div key={entry.id} className="flex items-center justify-between p-2 text-sm"><span>{entry.display_name}<span className="ml-2 text-xs text-slate-400">{entry.requires_bank_reconciliation ? "Bank reconciled" : "Direct"}</span></span><Button size="sm" variant="ghost" onClick={() => toggleMethod(entry)}>{entry.status === "active" ? "Deactivate" : "Activate"}</Button></div>)}</div></section>
    <section className="space-y-3 border-t pt-4"><h3 className="font-semibold">Bank Accounts</h3><div className="grid sm:grid-cols-3 gap-2"><Input value={bank.name} onChange={(e) => setBank({ ...bank, name: e.target.value })} placeholder="Bank name" /><Input value={bank.aliases} onChange={(e) => setBank({ ...bank, aliases: e.target.value })} placeholder="Aliases, comma separated" /><Input value={bank.currency} onChange={(e) => setBank({ ...bank, currency: e.target.value })} placeholder="MYR" /></div><Button onClick={addBank}><Plus className="h-4 w-4 mr-1" />Add Bank</Button><div className="divide-y rounded border">{banks.map((entry) => <div key={entry.id} className="flex items-center justify-between p-2 text-sm"><span>{entry.name} · {entry.currency}<span className="ml-2 text-xs text-slate-400">{entry.aliases?.join(", ")}</span></span><Button size="sm" variant="ghost" onClick={() => toggleBank(entry)}>{entry.active ? "Deactivate" : "Activate"}</Button></div>)}</div></section>
  </DialogContent></Dialog>;
}

export default function SalesPerformance() {
  const { range } = useDateRange();
  const { canManage, branches } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]), [banks, setBanks] = useState([]), [registryOpen, setRegistryOpen] = useState(false);
  const [targets, setTargets] = useState({});     // branch_id -> {value, status, error}
  const [branchFilter, setBranchFilter] = useState("__all__");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: methodData }, { data: bankData }] = await Promise.all([
        api.get("/sales/performance", { params: { start: range.start, end: range.end, ...(branchFilter === "__all__" ? {} : { branch_id: branchFilter }) } }),
        api.get("/sales/methods"), api.get("/sales/banks"),
      ]);
      setData(data);
      setMethods(methodData); setBanks(bankData);
      const t = {};
      data.branches.forEach((b) => (t[b.branch_id] = { value: b.target || 0, status: "idle" }));
      setTargets(t);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async (branchId) => {
    const y = Number(range.start.slice(0, 4));
    const m = Number(range.start.slice(5, 7));
    setTargets((p) => ({ ...p, [branchId]: { ...p[branchId], status: "saving" } }));
    try {
      await api.post("/branches/targets", { branch_id: branchId, year: y, month: m, amount: Number(targets[branchId].value) });
      setTargets((p) => ({ ...p, [branchId]: { ...p[branchId], status: "saved" } }));
      toast.success("Target saved");
      load();
    } catch (e) {
      setTargets((p) => ({ ...p, [branchId]: { ...p[branchId], status: "failed", error: apiError(e) } }));
      toast.error(apiError(e));
    }
  };

  const t = data?.totals;
  return (
    <div>
      <PageHeader title="Sales Performance"
        subtitle="Boss-facing waterfall. Bank received is the source of truth; BO figures remain comparison references."
        testid="performance-header" actions={<div className="flex gap-2"><div className="w-56"><BranchSelect value={branchFilter} onChange={setBranchFilter} allowAll testid="performance-branch-filter" /></div>{canManage() && <Button variant="outline" onClick={() => setRegistryOpen(true)}><Settings2 className="h-4 w-4 mr-1" />Sales Registry</Button>}</div>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {loading ? Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />) : (
          <>
            <KpiCard label="1 · Gross Sales" value={t.gross_sales} testid="kpi-gross-sales" />
            <KpiCard label="2 · Discounts" value={t.discounts} accent="amber" testid="kpi-discounts" />
            <KpiCard label="3 · Refunds" value={t.refunds} accent="red" testid="kpi-refunds" />
            <KpiCard label="4 · Merchant Fees" value={t.merchant_fees} accent="amber" testid="kpi-merchant-fees" />
            {data.sales_methods.map((method, index) => <KpiCard key={method.id} label={`${index + 5} · ${method.display_name}`} value={t.method_received?.[method.id] || 0} accent={method.color === "emerald" ? "green" : "slate"} />)}
            <KpiCard label={`${data.sales_methods.length + 5} · Net Sales`} value={t.net_sales} accent="blue" testid="kpi-net-sales" />
            <KpiCard label="Total Cash Out" value={t.cash_out} accent="amber" testid="kpi-cashout" />
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Branch Performance</h2>
          <span className="text-xs text-slate-400">Target for month {range.start.slice(0, 7)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="performance-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 px-4">Branch</th>
                <th className="py-3 px-4 text-right">BO Credit</th>
                <th className="py-3 px-4 text-right">Merchant Fees</th>
                <th className="py-3 px-4 text-right">Net Sales</th>
                {data?.sales_methods?.map((method) => <th key={method.id} className="py-3 px-4 text-right">{method.display_name}</th>)}
                <th className="py-3 px-4 text-right">Total Actual</th>
                <th className="py-3 px-4">Monthly Target</th>
                <th className="py-3 px-4 w-44">Achievement</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={(data?.sales_methods?.length || 3) + 8} className="p-6"><Skeleton className="h-40 w-full" /></td></tr>
              ) : data.branches.length === 0 ? (
                <tr><td colSpan={(data?.sales_methods?.length || 3) + 8} className="text-center py-10 text-slate-400">No sales data in this period yet.</td></tr>
              ) : data.branches.map((b) => {
                const tg = targets[b.branch_id] || { value: 0, status: "idle" };
                const pct = b.target ? Math.min(100, (b.total_actual / b.target) * 100) : 0;
                return (
                  <tr key={b.branch_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perf-row-${b.branch_code}`}>
                    <td className="py-3 px-4 font-medium text-slate-800">{b.branch_name}
                      {!b.active && <span className="ml-2 text-[10px] text-slate-400">(inactive)</span>}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-slate-500">{rm(b.bo_credit)}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-amber-600">{rm(b.merchant_fees)}</td>
                    <td className="py-3 px-4 text-right tabular-nums font-semibold text-blue-700">{rm(b.net_sales)}</td>
                    {data.sales_methods.map((method) => <td key={method.id} className="py-3 px-4 text-right tabular-nums">{rm(b.method_received?.[method.id] || 0)}</td>)}
                    <td className="py-3 px-4 text-right tabular-nums font-bold text-blue-700">{rm(b.total_actual)}</td>
                    <td className="py-3 px-4">
                      {b.branch_code === "—" ? <span className="text-slate-400 text-xs">—</span> : !canManage() ? (
                        <span className="text-sm tabular-nums">{b.target ? rm(b.target) : "—"}</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input type="number" value={tg.value} data-testid={`target-input-${b.branch_code}`}
                            onChange={(e) => setTargets((p) => ({ ...p, [b.branch_id]: { ...p[b.branch_id], value: e.target.value, status: "idle" } }))}
                            className="h-8 w-28 text-right text-xs" />
                          <Button size="sm" variant="secondary" className="h-8 px-2 text-xs"
                            data-testid={`target-save-${b.branch_code}`} onClick={() => saveTarget(b.branch_id)}>Save</Button>
                        </div>
                      )}
                      <div className="mt-1"><SaveStatus status={tg.status} error={tg.error} /></div>
                    </td>
                    <td className="py-3 px-4">
                      {b.target ? (
                        <div>
                          <Progress value={pct} className="h-2" />
                          <div className="text-[11px] text-slate-500 mt-1">
                            {b.achievement?.toFixed(1)}% · Var {rm(b.variance_from_target)}
                          </div>
                        </div>
                      ) : <span className="text-xs text-slate-400">No target set</span>}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {b.branch_code !== "—" && (
                        <Button size="sm" variant="ghost" className="h-8 text-xs text-blue-600"
                          data-testid={`drill-${b.branch_code}`} onClick={() => navigate("/sales/credit")}>
                          Details <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && data.branches.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold text-slate-800 bg-slate-50">
                  <td className="py-3 px-4">Company Total</td>
                  <td className="py-3 px-4 text-right tabular-nums text-slate-500">{rm(t.bo_credit)}</td>
                  <td className="py-3 px-4 text-right tabular-nums text-amber-600">{rm(t.merchant_fees)}</td>
                  <td className="py-3 px-4 text-right tabular-nums text-blue-700">{rm(t.net_sales)}</td>
                  {data.sales_methods.map((method) => <td key={method.id} className="py-3 px-4 text-right tabular-nums">{rm(t.method_received?.[method.id] || 0)}</td>)}
                  <td className="py-3 px-4 text-right tabular-nums text-blue-700">{rm(t.total_actual)}</td>
                  <td className="py-3 px-4 text-right tabular-nums">{rm(t.target)}</td>
                  <td className="py-3 px-4" colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <RegistryManager open={registryOpen} setOpen={setRegistryOpen} methods={methods} banks={banks} branches={branches} refresh={load} />
    </div>
  );
}
